import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../../auth';
import './NumberAssistantProfile.css';

// Mirrors server openclawState.normNum — digits only, drop US country code.
function normNum(n) {
  const d = String(n == null ? '' : n).replace(/[^\d]/g, '');
  return (d.length === 11 && d.charAt(0) === '1') ? d.slice(1) : d;
}

const ACCESS_RESOURCES = [
  { id: 'contacts', label: 'Contacts' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'calendar_availability', label: 'Calendar Availability' },
  { id: 'notes', label: 'Notes' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'sms', label: 'SMS' },
  { id: 'dictation', label: 'Dictation' },
  { id: 'linktime', label: 'LinkTime' },
  { id: 'web', label: 'Web' },
  { id: 'travel', label: 'Travel' },
  { id: 'email', label: 'Email' },
  { id: 'system', label: 'System' },
  { id: 'conversations', label: 'Conversations' },
];

/**
 * Per-number AI Assistant profile editor. Three independent overlays on the
 * account default: Persona (Marketplace template), Access (RBAC matrix),
 * Skills (lightweight know-how packs). Anything left at default is inherited.
 */
export default function NumberAssistantProfile({ number, displayNumber, onClose, onSaved }) {
  const [tab, setTab] = useState('persona');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');

  const [defaultAccess, setDefaultAccess] = useState({});
  const [prompts, setPrompts] = useState([]);
  const selectedRowRef = useRef(null);

  const [profileId, setProfileId] = useState(null);
  const [customAccess, setCustomAccess] = useState(false); // false = inherit default
  const [access, setAccess] = useState({});
  const [skills, setSkills] = useState([]); // [{name, instruction}]

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [npRes, cfgRes, mpRes] = await Promise.all([
        authFetch('/api/aiassistant/number-profiles'),
        authFetch('/api/aiassistant/config'),
        authFetch('/api/marketplace/prompts?limit=100'),
      ]);
      const np = npRes.ok ? await npRes.json() : { numberProfiles: {} };
      const cfg = cfgRes.ok ? await cfgRes.json() : {};
      const mp = mpRes.ok ? await mpRes.json() : [];
      const p = (np.numberProfiles || {})[normNum(number)] || {};
      setDefaultAccess((cfg && cfg.access) || {});
      setProfileId(p.profileId || null);
      setCustomAccess(!!p.access);
      setAccess(p.access ? JSON.parse(JSON.stringify(p.access)) : JSON.parse(JSON.stringify((cfg && cfg.access) || {})));
      setSkills(Array.isArray(p.skills) ? p.skills.map(s => ({ name: s.name || '', instruction: s.instruction || '' })) : []);
      setPrompts(Array.isArray(mp) ? mp : []);
    } catch (e) {
      setError('Could not load profile settings.');
    }
    setLoading(false);
  }, [number]);

  useEffect(() => { load(); }, [load]);

  // Once the persona list loads, scroll the pre-selected template into view so it's
  // not hidden below the fold (e.g. Private Driver sits near the bottom of the list).
  useEffect(() => {
    if (profileId && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'center' });
    }
  }, [prompts]); // eslint-disable-line react-hooks/exhaustive-deps

  const put = async (body) => {
    setSaving(true); setSavedMsg(''); setError('');
    try {
      const res = await authFetch('/api/aiassistant/number-profile/' + encodeURIComponent(normNum(number)), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + res.status)); }
      setSavedMsg('Saved.');
      if (onSaved) onSaved();
    } catch (e) { setError(e.message || 'Save failed.'); }
    setSaving(false);
  };

  const savePersona = () => put({ profileId: profileId });
  const saveAccess = () => put({ access: customAccess ? access : null });
  const saveSkills = () => put({ skills: skills.filter(s => (s.name || '').trim()) });

  const resetAll = async () => {
    setSaving(true); setSavedMsg(''); setError('');
    try {
      const res = await authFetch('/api/aiassistant/number-profile/' + encodeURIComponent(normNum(number)), { method: 'DELETE' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setProfileId(null); setCustomAccess(false);
      setAccess(JSON.parse(JSON.stringify(defaultAccess))); setSkills([]);
      setSavedMsg('Reset to account default.');
      if (onSaved) onSaved();
    } catch (e) { setError('Reset failed.'); }
    setSaving(false);
  };

  const togglePerm = (resource, perm) => {
    setAccess(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[resource]) next[resource] = {};
      next[resource][perm] = !(next[resource][perm] === true);
      if (!next[resource].read && !next[resource].write && !next[resource].delete) delete next[resource];
      return next;
    });
    setSavedMsg('');
  };

  return (
    <div className="nap-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nap-dialog" role="dialog" aria-modal="true">
        <div className="nap-head">
          <div>
            <h3>AI Assistant — {displayNumber || number}</h3>
            <p className="nap-sub">Customize this number. Anything you leave alone uses your account default.</p>
          </div>
          <button className="nap-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="nap-tabs">
          {['persona', 'access', 'skills'].map(t => (
            <button key={t} className={`nap-tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); setSavedMsg(''); setError(''); }}>
              {t === 'persona' ? 'Persona' : t === 'access' ? 'Access' : 'Skills'}
            </button>
          ))}
        </div>

        <div className="nap-body">
          {loading ? <div className="nap-loading">Loading…</div> : (
            <>
              {tab === 'persona' && (
                <div className="nap-section">
                  <p className="nap-desc">Pick a Marketplace template for this number, or use your default assistant.</p>
                  <label className={`nap-persona-row ${!profileId ? 'sel' : ''}`}>
                    <input type="radio" name="persona" checked={!profileId} onChange={() => setProfileId(null)} />
                    <span><strong>Account default</strong><br /><span className="nap-muted">Inherit your main assistant persona</span></span>
                  </label>
                  {prompts.map(p => (
                    <label key={p.id} ref={profileId === p.id ? selectedRowRef : null} className={`nap-persona-row ${profileId === p.id ? 'sel' : ''}`}>
                      <input type="radio" name="persona" checked={profileId === p.id} onChange={() => setProfileId(p.id)} />
                      <span><strong>{p.name}</strong><br /><span className="nap-muted">{p.description || p.category}</span></span>
                    </label>
                  ))}
                  {prompts.length === 0 && <p className="nap-muted">No Marketplace templates yet — browse the Marketplace to publish or install one.</p>}
                  <button className="nap-save" disabled={saving} onClick={savePersona}>Save Persona</button>
                </div>
              )}

              {tab === 'access' && (
                <div className="nap-section">
                  <label className="nap-toggle">
                    <input type="checkbox" checked={customAccess} onChange={(e) => {
                      const on = e.target.checked;
                      setCustomAccess(on);
                      if (on && Object.keys(access).length === 0) setAccess(JSON.parse(JSON.stringify(defaultAccess)));
                      setSavedMsg('');
                    }} />
                    <span>Custom permissions for this number {customAccess ? '' : '(off = inherit account default)'}</span>
                  </label>
                  <div className={`nap-grid ${customAccess ? '' : 'nap-grid-disabled'}`}>
                    <div className="nap-grid-head"><span>Resource</span><span>Read</span><span>Write</span><span>Delete</span></div>
                    {ACCESS_RESOURCES.map(r => {
                      const perms = access[r.id] || {};
                      return (
                        <div key={r.id} className="nap-grid-row">
                          <span>{r.label}</span>
                          <input type="checkbox" disabled={!customAccess} checked={perms.read === true} onChange={() => togglePerm(r.id, 'read')} />
                          <input type="checkbox" disabled={!customAccess} checked={perms.write === true} onChange={() => togglePerm(r.id, 'write')} />
                          <input type="checkbox" disabled={!customAccess} checked={perms.delete === true} onChange={() => togglePerm(r.id, 'delete')} />
                        </div>
                      );
                    })}
                  </div>
                  <button className="nap-save" disabled={saving} onClick={saveAccess}>Save Access</button>
                </div>
              )}

              {tab === 'skills' && (
                <div className="nap-section">
                  <p className="nap-desc">Skills are know-how the assistant follows on this number (e.g. how to take reservations). They don't grant tools — set those under Access.</p>
                  {skills.map((s, i) => (
                    <div key={i} className="nap-skill">
                      <input className="nap-skill-name" placeholder="Skill name (e.g. Reservations)" value={s.name}
                        onChange={(e) => setSkills(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                      <textarea className="nap-skill-instr" placeholder="How to do it…" value={s.instruction}
                        onChange={(e) => setSkills(prev => prev.map((x, j) => j === i ? { ...x, instruction: e.target.value } : x))} />
                      <button className="nap-skill-del" onClick={() => setSkills(prev => prev.filter((_, j) => j !== i))} aria-label="Remove skill">✕</button>
                    </div>
                  ))}
                  <button className="nap-add" onClick={() => setSkills(prev => [...prev, { name: '', instruction: '' }])}>+ Add skill</button>
                  <button className="nap-save" disabled={saving} onClick={saveSkills}>Save Skills</button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="nap-foot">
          {error ? <span className="nap-err">{error}</span> : <span className="nap-ok">{savedMsg}</span>}
          <div className="nap-foot-actions">
            <button className="nap-reset" disabled={saving} onClick={resetAll}>Reset to default</button>
            <button className="nap-done" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
